/** @jsx jsx */

import React, {
  useContext,
  useRef,
  useLayoutEffect,
  Fragment,
  useEffect,
} from 'react';
import { css, jsx } from '@emotion/core';
import { colors, getButtonFocus } from 'styles/variables';
import { interactionContext as _interactionContext } from './interactionContext';
import { MultichoiceContext } from './Multichoice_v2';
import isIE from 'services/isIE';
import isEDGE from 'services/isEDGE';

import { getMultichoiceData } from 'services/scorm/scormService';

const { lPink, lGrey0, lTeal0 } = colors;

const Radio = props => {
  let {
    id,
    selected,
    updateSelected,
    correct,
    resolved,
    checkboxColor,
    style,
    isMulti,
    isSquare,
  } = props;

  // contexts
  const interactionContext: any = useContext(_interactionContext);
  const { showCorrectAnswer } = interactionContext.props;

  let savedMultichoiceData = getMultichoiceData();

  let lockedBySCORM;
  if (savedMultichoiceData && savedMultichoiceData[interactionContext.id]) {
    savedMultichoiceData = savedMultichoiceData[interactionContext.id];
    //
    selected = savedMultichoiceData.selected.includes(id);
    resolved = true;
    lockedBySCORM = true;
  }

  return (
    <Fragment>
      <div
        css={css`
          all: unset;
          position: relative;
          flex: 0 0 auto;
          width: 20px;
          height: 20px;
          ${!isMulti && !isSquare && 'border-radius: 50%;'}
          border: 1px solid ${checkboxColor};
          outline: ${checkboxColor};
          background: ${selected ? checkboxColor : 'transparent'};
          ${!!showCorrectAnswer &&
            resolved &&
            correct &&
            'background: transparent;'}
          cursor: pointer;

          &:hover {
            opacity: 0.8;
            background: ${selected ? checkboxColor : 'transparent'};
          }

          &:focus {
border: 10px solid red;
display: none;
          }

          ${(isIE() || lockedBySCORM) && 'pointer-events: none;'}
        `}
        style={style}
        onClick={
          window.dev()
            ? e => {
                e.stopPropagation();
                updateSelected(id);
              }
            : () => {}
        }
      >
        {!!showCorrectAnswer && resolved && correct && (
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              width: 100%;
              height: 100%;
            `}
          >
            <Tick
              type={isMulti || isSquare ? 'checkbox' : 'radio'}
              color={checkboxColor}
            />
          </div>
        )}
      </div>
    </Fragment>
  );
};

const NewTick = ({ width = 9, height = 7, color }) => {
  // updated again. . .
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="10"
      height="10"
      viewBox="0 0 8 8"
    >
      <g>
        <path
          style={{
            stroke: color,
            strokeWidth: 1.5,
            strokeMiterlimit: 10,
            fill: 'none',
          }}
          d="M.5,4.46l2.41,2L7.44,1.55"
        />
      </g>
    </svg>
  );
};

const Tick = ({ type = 'radio', width = 11, height = 11, color }) => {
  return (
    <div
      css={css({
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: 20,
        height: 20,
      })}
    >
      <NewTick color={color} />
    </div>
  );
};

const Answer = props => {
  const { id, children, style = {} } = props;

  // contexts
  const interactionContext = useContext<any>(_interactionContext);
  const multichoiceContext = useContext<any>(MultichoiceContext);

  // refs
  const answer = useRef<HTMLDivElement | null>(null);
  const label = useRef(null);
  const optionText = useRef<HTMLDivElement | null>(null);

  const {
    panelBackground,
    panelBorderRadius = 5,
    optionTextColor,
    checkboxColor,
    dropShadow = false,
  } = interactionContext.props;

  let {
    selected,
    correct,
    updateSelected,
    submit,
    isMultiSelect,
    answerCount,
  } = multichoiceContext;

  // console.log(correct, id);

  correct = (correct as any).includes(id);
  const resolved = submit === 2;

  useLayoutEffect(() => {
    // console.log('optionText.current :', optionText.current);
    if (!optionText.current) {
      return;
    }
    //
    // remove the chameleon-text class
    // so only responds to local color
    const chameleonText = optionText.current.querySelector('.chameleon-text');
    if (chameleonText) {
      // can't use replace without polyfill for ie
      // this will do
      chameleonText.classList.remove('chameleon-text');
      chameleonText.classList.add('chameleon-text-multichoice-answer');
    }
  });

  useEffect(() => {
    if (!isIE()) {
      return;
    }

    const checkAnswerHeight = () => {
      answer.current!.style.height = `${answer.current!.scrollHeight}px`;
    };

    const poll = setInterval(() => {
      const answerHeight = answer.current!.scrollHeight;
      if (answerHeight) {
        // console.log('in IE11. answerHeight :', answerHeight);
        answer.current!.style.height = `${answerHeight}px`;
        clearInterval(poll);
      }
    }, 1000 / 60);

    window.addEventListener('resize', checkAnswerHeight);

    return () => {
      window.removeEventListener('resize', checkAnswerHeight);
    };
  }, []);

  //
  //

  let boxShadow: string | undefined = undefined;

  if (window.dev() && correct) {
    boxShadow = `0 0 0 1px #fff, 0 0 0 3px ${lPink}`;
  }

  if (dropShadow) {
    boxShadow = `${
      boxShadow ? `${boxShadow}, ` : ''
    }0px 0px 6px rgba(0, 0, 0, 0.15)`;
  }

  return (
    <div
      ref={answer}
      css={css`
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        min-height: 60px;
        margin: 7.5px 0;
        padding: 8px 15px;
        border: none;
        outline: none;
        border-radius: ${panelBorderRadius}px;
        text-align: left;
        background: ${panelBackground};

        color: ${optionTextColor};

        li {
          color: #fff !important;
        }

        li span {
          color: ${optionTextColor};
        }

        box-shadow: ${boxShadow};

        cursor: pointer;
        pointer-events: ${submit === 0 ? 'auto' : 'none'};

        &:focus-visible {
          ${getButtonFocus(panelBackground)}
        }

        ${resolved && 'pointer-events: none;'}
      `}
      onKeyUp={
        window.dev()
          ? () => {}
          : (e: any) => {
              if ([' ', 'Enter'].includes(e.key)) {
                updateSelected(id);
              }
            }
      }
      onClick={
        window.dev()
          ? () => {}
          : (e: any) => {
              updateSelected(id);
            }
      }
      tabIndex={resolved ? -1 : 0}
    >
      {!window.dev() && (
        <input
          id={`i${id}`}
          type={isMultiSelect ? 'checkbox' : 'radio'}
          value={`i${id}`}
          name={interactionContext.props.id}
          checked={(selected as any).includes(id)}
          readOnly
          css={css`
            position: absolute;
            left: 0;
            top: 0;
            opacity: 0;
          `}
          tabIndex={-1}
          disabled={resolved}
        />
      )}

      <label
        htmlFor={`i${id}`}
        ref={label}
        css={css`
          display: flex;
          flex: 1;

          ${!isIE() &&
            !isEDGE() &&
            `* {
        overflow-wrap: break-word;
        word-break: break-all;
        word-break: break-word;
        // unsetting this little bugger. be browser default
        // hyphens: auto;
      }`}

          * div {
            color: ${optionTextColor};
          }

          * p {
            margin: 0px;
          }

          ${!window.dev() && 'pointer-events: none;'}
        `}
        tabIndex={-1}
      >
        <div
          ref={optionText}
          css={css`
            width: 100%;
            padding-right: 15px;
          `}
        >
          {children}
        </div>
      </label>
      <Radio
        id={id}
        isMulti={isMultiSelect}
        isSquare={answerCount === 1}
        correct={correct}
        selected={(selected as any).includes(id)}
        updateSelected={updateSelected}
        resolved={resolved}
        checkboxColor={checkboxColor}
        style={style}
      />
    </div>
  );
};

export default Answer;
